import { Router } from '@vaadin/router';
import { API_BASE_URL } from '../api';
import { AuthAPI } from '../api/auth';
import { RefreshResponseData } from '../types/api/auth/auth';

/**
 * Manages authentication tokens (access token and refresh token) for client-side applications.
 * This class handles storing and retrieving tokens from localStorage, as well as refreshing the access token using an API.
 */
export class AuthManager {
  private refreshTimerId: number | null = null;
  private refreshInFlight: Promise<string | null> | null = null;
  private monitoringInitialized = false;

  private static readonly REFRESH_SKEW_MS = 60_000;
  private static readonly MIN_REFRESH_DELAY_MS = 5_000;

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Retrieves the refresh token from local storage.
   *
   * @return {string | null} The refresh token retrieved from local storage, or null if not found.
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Stores the provided access and refresh tokens into local storage.
   *
   * @param {string} accessToken - The access token to be stored.
   * @param {string} refreshToken - The refresh token to be stored.
   * @return {void} This method does not return a value.
   */
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    this.scheduleRefreshFromAccessToken();
  }

  /**
   * Clears the stored authentication tokens from the local storage.
   *
   * @return {void} This method does not return any value.
   */
  clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    this.clearRefreshTimer();
  }

  initializeSessionMonitoring(): void {
    if (this.monitoringInitialized) return;

    this.monitoringInitialized = true;

    window.addEventListener('focus', () => {
      void this.maybeRefreshOnForeground();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.maybeRefreshOnForeground();
      }
    });

    void this.maybeRefreshOnForeground();
  }

  /**
   * Refreshes the access token using the stored refresh token.
   * If the refresh token is not available or the operation fails, it clears all tokens and returns null.
   *
   * @return {Promise<string | null>} A promise that resolves to the new access token if successful, or null if unsuccessful.
   */
  async refreshAccessToken(): Promise<string | null> {
    if (this.refreshInFlight) {
      return await this.refreshInFlight;
    }

    this.refreshInFlight = this.performRefreshAccessToken();

    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async performRefreshAccessToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;

    const base = API_BASE_URL.replace(/\/+$/, '');
    const url = `${base}${AuthAPI.refresh()}`;

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) {
        if (res.status === 400 || res.status === 401 || res.status === 403) {
          this.clearTokens();
        }
        return null;
      }
      const data = (await res.json()) as RefreshResponseData;

      /*const res = await apiPost<RefreshResponseData>(
        AuthAPI.refresh(),
        { refresh_token: refreshToken },
        { auth: false }
      );*/

      const newAccessToken = data.access_token ?? null;

      if (!newAccessToken) {
        localStorage.removeItem('access_token');
        this.clearRefreshTimer();
        return null;
      }

      localStorage.setItem('access_token', newAccessToken);
      this.scheduleRefreshFromAccessToken();
      return newAccessToken;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_err) {
      return null;
    }
  }

  private async maybeRefreshOnForeground(): Promise<void> {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      this.clearRefreshTimer();
      return;
    }

    const expiresAtMs = this.getJwtExpiryMs(accessToken);

    // If token format is unknown, keep passive behavior and avoid aggressive refresh loops.
    if (!expiresAtMs) {
      this.clearRefreshTimer();
      return;
    }

    const shouldRefreshNow = Date.now() >= expiresAtMs - AuthManager.REFRESH_SKEW_MS;
    if (shouldRefreshNow) {
      await this.refreshAccessToken();
      return;
    }

    this.scheduleRefreshFromAccessToken();
  }

  private scheduleRefreshFromAccessToken(): void {
    this.clearRefreshTimer();

    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    const expiresAtMs = this.getJwtExpiryMs(accessToken);
    if (!expiresAtMs) return;

    const delayMs = expiresAtMs - Date.now() - AuthManager.REFRESH_SKEW_MS;
    const safeDelayMs = Math.max(delayMs, AuthManager.MIN_REFRESH_DELAY_MS);

    this.refreshTimerId = window.setTimeout(() => {
      void this.refreshAccessToken();
    }, safeDelayMs);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerId !== null) {
      window.clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  private getJwtExpiryMs(token: string): number | null {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    try {
      const payload = JSON.parse(this.decodeBase64Url(parts[1])) as { exp?: number };
      if (typeof payload.exp !== 'number') return null;
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  private decodeBase64Url(value: string): string {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return atob(padded);
  }

  logoutAndRedirect(): void {
    this.clearTokens();
    Router.go('/login');
  }
}

// Singleton-Instanz, die überall genutzt werden kann
export const authManager = new AuthManager();
